import { useMemo } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { useClimate } from '~/crop/crop-schedule/hooks/use-climate'

// ─── Math helpers ─────────────────────────────────────────────────────────────

// Normal PDF normalized so that peak = 1 (for display scaling only)
const normalPdf = (x, mean, std) => {
  const z = (x - mean) / std
  return Math.exp(-0.5 * z * z)
}

// Normal CDF via Abramowitz & Stegun approximation (max error < 1.5e-7)
const normalCdf = (x, mean, std) => {
  const z = (x - mean) / (std * Math.SQRT2)
  const absZ = Math.abs(z)
  const t = 1 / (1 + 0.3275911 * absZ)
  const poly = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))))
  const erf = 1 - poly * Math.exp(-absZ * absZ)
  return 0.5 * (1 + (z >= 0 ? erf : -erf))
}

// Day-of-year from a dayjs date (1 = Jan 1)
const toDOY = (date) => date.diff(date.startOf('year'), 'day') + 1

// Convert DOY to a short date label using the current year as reference
const doyToLabel = (doy) => dayjs().startOf('year').add(doy - 1, 'day').format('MMM D')

// Ordinal suffix helper
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0])
}

// ─── SVG layout constants ──────────────────────────────────────────────────────

const VB_W = 500
const VB_H = 130
const PAD_TOP = 12
const PAD_BOTTOM = 30  // space for x-axis date labels
const PAD_SIDE = 6
const CHART_W = VB_W - PAD_SIDE * 2
const CHART_H = VB_H - PAD_TOP - PAD_BOTTOM
const CURVE_SAMPLES = 150

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Renders a normal distribution bell curve for lfd/ffd frost dates,
 * with the selected report date marked as a vertical line alongside statistics.
 *
 * @param {'lfd'|'ffd'} reportCode
 * @param {import('dayjs').Dayjs} reportDate
 * @param {string} farmId
 */
export const FrostDistributionChart = ({ reportCode, reportDate, farmId }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { climateData, loading } = useClimate({ farmId })

  const mean = useMemo(() => {
    if (!climateData) return null
    return reportCode === 'lfd' ? climateData.avg_lfd : climateData.avg_ffd
  }, [climateData, reportCode])

  const std = useMemo(() => {
    if (!climateData) return null
    const raw = reportCode === 'lfd' ? climateData.std_lfd : climateData.std_ffd
    return raw && raw > 0 ? raw : null
  }, [climateData, reportCode])

  // X-axis tick DOY values: μ ± 2σ, μ ± σ, μ
  const sigmaPoints = useMemo(() => {
    if (mean == null || std == null) return []
    return [-2, -1, 0, 1, 2].map(k => mean + k * std)
  }, [mean, std])

  // SVG paths computed from bell curve geometry
  const svgPaths = useMemo(() => {
    if (mean == null || std == null) return null

    const minDoy = mean - 3 * std
    const maxDoy = mean + 3 * std
    const range = maxDoy - minDoy

    const doyToX = (doy) => PAD_SIDE + ((doy - minDoy) / range) * CHART_W
    const pdfToY = (pdf) => PAD_TOP + CHART_H * (1 - pdf)  // pdf=1 → top, pdf=0 → baseline
    const baseY = pdfToY(0)

    const pts = Array.from({ length: CURVE_SAMPLES + 1 }, (_, i) => {
      const doy = minDoy + (i / CURVE_SAMPLES) * range
      return { x: doyToX(doy), y: pdfToY(normalPdf(doy, mean, std)) }
    })

    // Full bell curve outline
    const curvePath = pts
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' ')

    // ±2σ filled band
    const x2lo = doyToX(mean - 2 * std)
    const x2hi = doyToX(mean + 2 * std)
    const pts2 = pts.filter(p => p.x >= x2lo - 0.5 && p.x <= x2hi + 0.5)
    const path2sigma = pts2.length
      ? `M${x2lo.toFixed(1)},${baseY} ${pts2.map(p => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} L${x2hi.toFixed(1)},${baseY} Z`
      : ''

    // ±1σ filled band
    const x1lo = doyToX(mean - std)
    const x1hi = doyToX(mean + std)
    const pts1 = pts.filter(p => p.x >= x1lo - 0.5 && p.x <= x1hi + 0.5)
    const path1sigma = pts1.length
      ? `M${x1lo.toFixed(1)},${baseY} ${pts1.map(p => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} L${x1hi.toFixed(1)},${baseY} Z`
      : ''

    return { curvePath, path2sigma, path1sigma, doyToX, baseY }
  }, [mean, std])

  // Date marker + stats derived from selected reportDate
  const dateStats = useMemo(() => {
    if (!svgPaths || !reportDate || mean == null || std == null) return null
    const doy = toDOY(reportDate)
    const x = svgPaths.doyToX(doy)
    const diff = Math.round(doy - mean)
    const pct = Math.round(normalCdf(doy, mean, std) * 100)
    return { doy, x, diff, pct }
  }, [svgPaths, reportDate, mean, std])

  // ─── Colors from theme ──────────────────────────────────────────────────────
  const fill2sigma = isDark
    ? (theme.palette.primary[900] ?? 'rgba(25,118,210,0.15)')
    : (theme.palette.primary[50] ?? 'rgba(25,118,210,0.08)')
  const fill1sigma = isDark
    ? (theme.palette.primary[800] ?? 'rgba(25,118,210,0.3)')
    : (theme.palette.primary[100] ?? 'rgba(25,118,210,0.18)')
  const curveStroke = isDark ? theme.palette.primary[300] : theme.palette.primary.main
  const meanColor = theme.palette.text.secondary
  const dateColor = theme.palette.info.main
  const axisColor = theme.palette.text.secondary

  // ─── Loading / no-data states ───────────────────────────────────────────────
  if (loading) {
    return <Box sx={{ height: 130, borderRadius: 1, bgcolor: 'action.hover', mt: '0.75rem' }} />
  }

  if (!climateData || mean == null || std == null) {
    return (
      <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="text.disabled">
          No climate data available for this farm.
        </Typography>
      </Box>
    )
  }

  const baseY = PAD_TOP + CHART_H
  const meanX = svgPaths.doyToX(mean)

  // ─── Stat block text ─────────────────────────────────────────────────────
  const diffLabel = !dateStats ? null
    : dateStats.diff === 0 ? 'On the average date'
    : `${Math.abs(dateStats.diff)} days ${dateStats.diff < 0 ? 'earlier' : 'later'} than avg`

  const pctLabel = dateStats ? `${ordinal(dateStats.pct)} percentile` : null

  return (
    <Box sx={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
      {/* Bell curve SVG */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          width="100%"
          style={{ display: 'block' }}
          aria-hidden="true"
        >
          {/* ±2σ shaded band */}
          {svgPaths.path2sigma && (
            <path d={svgPaths.path2sigma} fill={fill2sigma} />
          )}

          {/* ±1σ shaded band */}
          {svgPaths.path1sigma && (
            <path d={svgPaths.path1sigma} fill={fill1sigma} />
          )}

          {/* Bell curve outline */}
          <path d={svgPaths.curvePath} fill="none" stroke={curveStroke} strokeWidth="1.5" />

          {/* Mean dashed line */}
          <line
            x1={meanX} y1={PAD_TOP}
            x2={meanX} y2={baseY}
            stroke={meanColor} strokeWidth="1" strokeDasharray="4,3"
          />

          {/* Selected date line + pin marker */}
          {dateStats && (
            <>
              <line
                x1={dateStats.x} y1={PAD_TOP}
                x2={dateStats.x} y2={baseY}
                stroke={dateColor} strokeWidth="2"
              />
              {/* Downward-pointing triangle pin above the line */}
              <polygon
                points={`${dateStats.x - 5},2 ${dateStats.x + 5},2 ${dateStats.x},${PAD_TOP}`}
                fill={dateColor}
              />
            </>
          )}

          {/* X-axis baseline */}
          <line
            x1={PAD_SIDE} y1={baseY}
            x2={VB_W - PAD_SIDE} y2={baseY}
            stroke={axisColor} strokeWidth="0.5"
          />

          {/* Tick labels */}
          {sigmaPoints.map((doy, i) => (
            <text
              key={i}
              x={svgPaths.doyToX(doy)}
              y={baseY + 14}
              textAnchor="middle"
              fontSize="9"
              fill={axisColor}
            >
              {doyToLabel(Math.round(doy))}
            </text>
          ))}
        </svg>
      </Box>

      {/* Stat block */}
      <Box sx={{ minWidth: 130, flexShrink: 0 }}>
        {diffLabel && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
            {diffLabel}
          </Typography>
        )}
        {pctLabel && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
            {pctLabel}
          </Typography>
        )}
        {!diffLabel && !pctLabel && (
          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
            Select a date to compare
          </Typography>
        )}
      </Box>
    </Box>
  )
}
